import React, { FC } from "react";
import { classNames } from "@utils/classNames";
import { SkuType, Sku } from ".prisma/client";
import { moneyFormatter } from "@utils/formatters/moneyFormatter";
import { Switch } from "@headlessui/react";
import {
  JobPostPublishSkuDescription,
  JobPostStickySkuDescription,
  JobPostThreeTweetsSkuDescription,
} from "@components/Content/SkuDescriptions";

const JOB_POST_PUBLISH_SKU_ID = "106c4537-02d2-4e63-8a77-c27aa166e2a9";

interface Props {
  availableSkus: Sku[];
  selectedSkuIds: string[];
  setSelectedSkuIds: (skuIds: Sku["id"][]) => void;
  onSubmit?(): void;
}

export const JobPostPublish: FC<Props> = ({
  availableSkus,
  selectedSkuIds,
  setSelectedSkuIds,
  onSubmit,
}) => {
  const sortedAvailableSkus = availableSkus.sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  const onToggleSkuId = (skuId: Sku["id"]) => {
    const ids = selectedSkuIds.includes(skuId)
      ? selectedSkuIds.filter((i) => i !== skuId)
      : [...selectedSkuIds, skuId];
    setSelectedSkuIds(ids);
  };

  // basket price
  const getBasketPriceAmount = (selectedSkuIds: Sku["id"][]) => {
    const amount = selectedSkuIds
      // get SKUs from skuIds
      .map((id) => availableSkus.find((sku) => sku.id === id))
      // get priceAmount
      .map((sku) => sku.priceAmount)
      // sum up
      .reduce((a, b) => a + b, 0);
    return amount;
  };
  const basketPriceAmount = getBasketPriceAmount(selectedSkuIds);
  const isDisabled = !selectedSkuIds.some(id => id === JOB_POST_PUBLISH_SKU_ID) && basketPriceAmount === 0;

  return (
    <div className="m-0.5 py-4 mb-6 shadow rounded-md bg-green-200">
      <div className="">
        {sortedAvailableSkus.map((sku, idx) => {
          const isSelected = selectedSkuIds.includes(sku.id);
          const isToggleDisabled = sku.isRequiredForJobPost;

          return (
            <div key={sku.id}>
              {sku.id === JOB_POST_PUBLISH_SKU_ID && (
                <div className="mb-2 px-4 text-xs font-bold uppercase text-green-900/60">
                  Publish
                </div>
              )}
              {availableSkus.some(({ id }) => id === JOB_POST_PUBLISH_SKU_ID)
                ? idx === 1 && (
                    <div className="mt-6 mb-2 px-4 text-xs font-bold uppercase text-green-900/60">
                      Boost visibility
                    </div>
                  )
                : idx === 0 && (
                    <div className="mb-2 px-4 text-xs font-bold uppercase text-green-900/60">
                      Boost visibility
                    </div>
                  )}
              <div
                key={sku.id}
                className={classNames(
                  "flex justify-between px-4 pt-2 pb-8 text-xs border-t border-green-900/10",
                  !isSelected && " bg-green-900/5"
                )}
              >
                <div className="w-full">
                  <div className="mb-2">
                    {!isToggleDisabled && (
                      <div className="pt-1">
                        <Switch
                          checked={isSelected}
                          onChange={() => onToggleSkuId(sku.id)}
                          className={classNames(
                            isSelected ? "bg-black" : "bg-green-900/10",
                            "relative inline-flex flex-shrink-0 h-4 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none"
                          )}
                        >
                          <span className="sr-only">Use setting</span>
                          <span
                            aria-hidden="true"
                            className={classNames(
                              isSelected ? "translate-x-5" : "translate-x-0",
                              "pointer-events-none inline-block h-3 w-3 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                            )}
                          />
                        </Switch>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex flex-row justify-between w-full mb-2 text-sm font-bold">
                      <div className="">
                        {sku.name}{" "}
                        {(sku.customProperties as any).boost && (
                          <span className="relative -top-0.5 inline-block ml-1 px-2 text-xxs font-medium text-green-900/90 bg-green-900/10 rounded-xl">
                            {(sku.customProperties as any).boost}
                          </span>
                        )}
                      </div>
                      <div className="">
                        {sku.priceAmount > 0
                          ? moneyFormatter.format(sku.priceAmount / 100)
                          : "Free"}
                      </div>
                    </div>
                    {sku.type === SkuType.JOB_POST_PUBLISH ? (
                      <JobPostPublishSkuDescription />
                    ) : sku.type === SkuType.JOB_POST_STICKY ? (
                      <JobPostStickySkuDescription />
                    ) : sku.type === SkuType.JOB_POST_THREE_TWEETS ? (
                      <JobPostThreeTweetsSkuDescription />
                    ) : (
                      <div>{sku.description}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex">
        <button
          type="button"
          className={classNames(
            isDisabled
              ? "text-black border-none bg-green-900/10"
              : "bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black",
            "inline-flex items-center mx-4 mt-4 mb-1 px-4 py-3 text-sm font-medium text-white border border-transparent shadow-sm rounded-md"
          )}
          onClick={onSubmit}
          disabled={isDisabled}
        >
          {basketPriceAmount
            ? `Checkout: ${moneyFormatter.format(basketPriceAmount / 100)}`
            : "Checkout: $0"}
        </button>
      </div>
    </div>
  );
};
